import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Rectangle,
  Popup,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  getReadingsForMap,
  getMapClusters,
  getEmployees,
  getTarifas,
  updateReading,
  createRoute,
} from "../services/api";
import {
  Search,
  Edit2,
  Save,
  X,
  RefreshCw,
  Layers,
  MapPin,
  Info,
  Navigation2,
  Trash2,
  CheckCircle2,
  MousePointerClick,
  RectangleHorizontal,
} from "lucide-react";

// Canvas renderers
const CANVAS_RENDERER = L.canvas({ padding: 0.5 }); // markers generales
const SELECTED_CANVAS = L.canvas({ padding: 0.5 }); // overlay de seleccionados
const ZOOM_CLUSTER = 18;
const DEBOUNCE_MS = 350;

// Replica exacta de la fórmula de grid del backend (adminController.js → getMapClusters)
function getGridForZoom(zoom) {
  const z = parseInt(zoom);
  if (z < 9)  return 0.5;
  if (z < 11) return 0.1;
  if (z < 13) return 0.02;
  if (z < 15) return 0.005;
  if (z < 17) return 0.001;
  if (z < 18) return 0.0003;
  if (z < 19) return 0.0001;
  return 0.00001;
}

// Calcula el bbox exacto de la celda del grid que contiene (lat, lng) a un zoom dado
function exactClusterBbox(lat, lng, zoom) {
  const grid = getGridForZoom(zoom);
  const cellLat = Math.round(lat / grid) * grid;
  const cellLng = Math.round(lng / grid) * grid;
  const eps = 1e-9; // epsilon para evitar errores de punto flotante en el BETWEEN
  return {
    minLat: cellLat - grid / 2 - eps,
    maxLat: cellLat + grid / 2 + eps,
    minLng: cellLng - grid / 2 - eps,
    maxLng: cellLng + grid / 2 + eps,
  };
}

// ── Convex hull (Andrew's monotone chain) ────────────────────────────────────

// ── Selected meters overlay (Leaflet nativo, sin reconciliación React) ───────
function SelectedMetersOverlay({ routeSelection, routeMode, onToggle }) {
  const map = useMapEvents({});
  const groupRef    = useRef(null);
  const onToggleRef = useRef(onToggle);

  // Mantener la ref del callback actualizada sin re-correr el efecto principal
  useEffect(() => { onToggleRef.current = onToggle; });

  useEffect(() => {
    if (!groupRef.current) {
      groupRef.current = L.layerGroup().addTo(map);
    }
    const group = groupRef.current;
    group.clearLayers();

    if (!routeMode || routeSelection.size === 0) return;

    routeSelection.forEach((m) => {
      const lat = parseFloat(m.latitude);
      const lng = parseFloat(m.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const circle = L.circleMarker([lat, lng], {
        radius: 5,
        color: "#b45309",
        fillColor: "#D4620D",
        fillOpacity: 0.95,
        weight: 1.5,
        renderer: SELECTED_CANVAS,
      });
      circle.on("click", () => onToggleRef.current(m));
      group.addLayer(circle);
    });

    return () => { group.clearLayers(); };
  }, [routeSelection, routeMode, map]);

  // Limpiar el grupo al desmontar
  useEffect(() => {
    return () => { groupRef.current?.remove(); groupRef.current = null; };
  }, [map]);

  return null;
}

// ── Map event handler ────────────────────────────────────────────────────────
function MapEventsHandler({ onBoundsChange }) {
  const map = useMapEvents({
    moveend: () => onBoundsChange(map),
    zoomend: () => onBoundsChange(map),
  });
  useEffect(() => {
    onBoundsChange(map);
  }, []); // carga inicial del viewport
  return null;
}

// ── Area selector (drag rectangle) ───────────────────────────────────────────
function AreaSelector({ active, appliedFilters, onSelected, onFetchStart, onFetchEnd }) {
  const [startPt, setStartPt] = useState(null);
  const [currPt,  setCurrPt]  = useState(null);
  const dragging = useRef(false);

  const map = useMapEvents({
    mousedown: (e) => {
      if (!active || e.originalEvent.ctrlKey) return;
      e.originalEvent.preventDefault();
      map.dragging.disable();
      dragging.current = true;
      setStartPt(e.latlng);
      setCurrPt(e.latlng);
    },
    mousemove: (e) => {
      if (!active || !dragging.current) return;
      setCurrPt(e.latlng);
    },
    mouseup: async (e) => {
      if (!active || !dragging.current) return;
      map.dragging.enable();
      dragging.current = false;

      const s = startPt;
      const c = e.latlng;
      setStartPt(null);
      setCurrPt(null);

      if (!s) return;
      // ignorar clicks muy pequeños (no fue un drag real)
      if (Math.abs(s.lat - c.lat) < 0.0002 && Math.abs(s.lng - c.lng) < 0.0002) return;

      const bbox = {
        minLat: Math.min(s.lat, c.lat), maxLat: Math.max(s.lat, c.lat),
        minLng: Math.min(s.lng, c.lng), maxLng: Math.max(s.lng, c.lng),
      };
      onFetchStart();
      try {
        const res = await getReadingsForMap({ ...bbox, limit: 5000, ...appliedFilters });
        const data = Array.isArray(res) ? res : res.data ?? [];
        onSelected(data);
      } catch (err) {
        console.error("Error fetching area meters:", err);
      } finally {
        onFetchEnd();
      }
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    container.style.cursor = active ? "crosshair" : "";
    return () => { container.style.cursor = ""; };
  }, [active, map]);

  if (!active || !startPt || !currPt) return null;

  const bounds = [
    [Math.min(startPt.lat, currPt.lat), Math.min(startPt.lng, currPt.lng)],
    [Math.max(startPt.lat, currPt.lat), Math.max(startPt.lng, currPt.lng)],
  ];

  return (
    <Rectangle
      bounds={bounds}
      pathOptions={{ color: "#16a34a", fillColor: "#16a34a", fillOpacity: 0.12, weight: 2, dashArray: "6 4" }}
    />
  );
}

// ── Cluster marker con número visible ────────────────────────────────────────
function clusterDivIcon(count, routeMode, isSelected) {
  const n    = count;
  const size = n < 10 ? 32 : n < 100 ? 38 : n < 1000 ? 44 : 52;
  const fs   = n < 10 ? 12 : n < 100 ? 13 : 14;
  // colores: seleccionado=naranja, modo ruta=verde, normal=azul
  const bg = isSelected
    ? "#D4620D"
    : routeMode
      ? "#16a34a"
      : "#2563eb";
  const shadow = isSelected
    ? "0 2px 10px rgba(212,98,13,0.5)"
    : routeMode
      ? "0 2px 8px rgba(22,163,74,0.4)"
      : "0 2px 8px rgba(37,99,235,0.4)";
  const border = isSelected ? "3px solid #fff" : "2.5px solid #fff";
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:${bg};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:800;font-size:${fs}px;
      border:${border};
      box-shadow:${shadow};
      font-family:Montserrat,sans-serif;
      cursor:pointer;
      user-select:none;
    ">${n.toLocaleString()}</div>`,
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function ClusterCircle({ cluster, routeMode, isSelected, onZoom, onCtrlSelect }) {
  const icon = clusterDivIcon(cluster.count, routeMode, isSelected);

  function handleClick(e) {
    if (routeMode && e.originalEvent?.ctrlKey) {
      e.originalEvent.preventDefault();
      onCtrlSelect(cluster);
    } else {
      onZoom();
    }
  }

  return (
    <Marker
      position={[cluster.lat, cluster.lng]}
      icon={icon}
      eventHandlers={{ click: handleClick }}
    >
      <Popup>
        <b>{cluster.count.toLocaleString()} medidores</b>
        {routeMode && (
          <div style={{ marginTop: 4, fontSize: 11, color: "#15803d" }}>
            Ctrl+Clic para seleccionar todo el cluster
          </div>
        )}
      </Popup>
    </Marker>
  );
}

// ── Individual meter dot ─────────────────────────────────────────────────────
const SEGMENTACION_COLORS = {
  residencial: { fill: "#16a34a", stroke: "#15803d" },
  comercial:   { fill: "#1e3a8a", stroke: "#1e40af" },
  default:     { fill: "#2563eb", stroke: "#1d4ed8" },
};

function MeterDot({ reading, isEditSelected, isRouteSelected, routeMode, onEditClick, onRouteToggle }) {
  const seg = SEGMENTACION_COLORS[reading.segmentacion] ?? SEGMENTACION_COLORS.default;
  let fillColor   = seg.fill;
  let strokeColor = seg.stroke;
  let radius = 5;

  if (isRouteSelected) {
    fillColor   = "#D4620D";
    strokeColor = "#b45309";
    radius = 7;
  } else if (isEditSelected) {
    fillColor   = "#D4620D";
    strokeColor = "#b45309";
    radius = 6;
  }

  function handleClick(e) {
    if (routeMode) {
      onRouteToggle(reading);
    } else {
      onEditClick(reading);
    }
  }

  return (
    <CircleMarker
      center={[reading.latitude, reading.longitude]}
      radius={radius}
      renderer={CANVAS_RENDERER}
      pathOptions={{ color: strokeColor, fillColor, fillOpacity: 0.9, weight: routeMode ? 1.5 : 1 }}
      eventHandlers={{ click: handleClick }}
    />
  );
}

// ── Route panel ──────────────────────────────────────────────────────────────
function RoutePanel({ selection, employees, tarifas, onRemove, onClear, onClose, onRouteCreated }) {
  const meters = Array.from(selection.values());
  const [form, setForm] = useState({ name: "", employeeId: "", valor: "", zona: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Cálculo de precio estimado según vehículo del empleado seleccionado
  const selectedEmployee = employees.find((e) => e.id === form.employeeId);
  const precioPorMedidor = parseFloat(tarifas?.precio_por_vehiculo?.[selectedEmployee?.vehicle_type]) || 0;
  const zonaPct = form.zona && tarifas?.zonas_porcentaje?.[form.zona]
    ? parseFloat(tarifas.zonas_porcentaje[form.zona]) : 0;
  const precioFinal = precioPorMedidor * (1 + zonaPct / 100);
  const valorPersonalizado = form.valor ? parseFloat(form.valor) : null;
  const precioEfectivo = valorPersonalizado ?? precioFinal;
  const totalEstimado = Math.round(meters.length * precioEfectivo);

  async function handleSave() {
    if (!form.name.trim()) { setError("El nombre de la ruta es requerido"); return; }
    if (meters.length === 0) { setError("Seleccioná al menos un medidor"); return; }
    setSaving(true);
    setError(null);
    try {
      const lats = meters.map((m) => parseFloat(m.latitude));
      const lngs = meters.map((m) => parseFloat(m.longitude));
      const buf  = 0.0005;
      const polygon = [
        [Math.min(...lats) - buf, Math.min(...lngs) - buf],
        [Math.max(...lats) + buf, Math.min(...lngs) - buf],
        [Math.max(...lats) + buf, Math.max(...lngs) + buf],
        [Math.min(...lats) - buf, Math.max(...lngs) + buf],
      ];
      const payload = {
        name: form.name.trim(),
        polygon,
        meter_ids: meters.map((m) => m.id), // IDs exactos — el backend los usa directamente
        zona: form.zona || null,
        employee_id: form.employeeId || null,
        valor_personalizado: totalEstimado > 0 ? totalEstimado : null,
      };
      await createRoute(payload);
      setSuccess(true);
      setTimeout(() => {
        onRouteCreated();
      }, 1000);
    } catch (e) {
      setError(e.message || "Error al crear la ruta");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={rp.panel}>
      {/* Header */}
      <div style={rp.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Navigation2 size={16} color="#16a34a" />
          <span style={rp.title}>Crear Ruta</span>
        </div>
        <button style={rp.closeBtn} onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      {/* Instrucciones */}
      <div style={rp.instructions}>
        <MousePointerClick size={12} style={{ flexShrink: 0, marginRight: 6 }} />
        <span>
          Hacé <b>clic</b> en puntos para seleccionar. <b>Ctrl+Clic</b> en un cluster
          para seleccionar todos sus medidores.
        </span>
      </div>

      {/* Counter */}
      <div style={rp.counter}>
        <span style={rp.counterNum}>{meters.length.toLocaleString()}</span>
        <span style={rp.counterLabel}>medidores seleccionados</span>
        {meters.length > 0 && (
          <button style={rp.clearSelBtn} onClick={onClear}>
            <Trash2 size={12} style={{ marginRight: 4 }} />
            Limpiar
          </button>
        )}
      </div>

      {/* Form */}
      <div style={rp.body}>
        <div style={rp.field}>
          <label style={rp.label}>Nombre de la ruta *</label>
          <input
            style={rp.input}
            placeholder="Ej: Ruta Zona Norte"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div style={rp.field}>
          <label style={rp.label}>Asignar auditor</label>
          <select
            style={rp.input}
            value={form.employeeId}
            onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}
          >
            <option value="">Sin asignar</option>
            {employees
              .filter((e) => e.role === "lector-plus")
              .map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
          </select>
        </div>
        <div style={rp.field}>
          <label style={rp.label}>Zona</label>
          <select
            style={rp.input}
            value={form.zona}
            onChange={(e) => setForm((f) => ({ ...f, zona: e.target.value }))}
          >
            <option value="">Sin zona</option>
            {["1","2","3","4"].map((z) => {
              const pct = tarifas?.zonas_porcentaje?.[z];
              return (
                <option key={z} value={z}>
                  Zona {z}{pct ? ` (+${pct}%)` : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div style={rp.field}>
          <label style={rp.label}>Valor personalizado ($)</label>
          <input
            style={rp.input}
            type="number"
            placeholder={precioPorMedidor ? `Base: $${precioFinal.toLocaleString("es-AR")} por medidor` : "Opcional"}
            value={form.valor}
            onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
          />
        </div>

        {/* Precio estimado */}
        {meters.length > 0 && precioEfectivo > 0 && (
          <div style={rp.priceBox}>
            <div style={rp.priceRow}>
              <span style={rp.priceLabel}>
                {meters.length.toLocaleString()} medidores × ${precioEfectivo.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
              </span>
            </div>
            {zonaPct > 0 && !valorPersonalizado && (
              <div style={rp.priceZone}>Incluye {zonaPct}% de zona {form.zona}</div>
            )}
            {valorPersonalizado && (
              <div style={rp.priceZone}>Precio personalizado aplicado</div>
            )}
            <div style={rp.priceTotal}>
              Total estimado: <strong>${totalEstimado.toLocaleString("es-AR")}</strong>
            </div>
          </div>
        )}

        {/* Selected meters list */}
        {meters.length > 0 && (
          <div style={rp.field}>
            <label style={rp.label}>Medidores ({meters.length})</label>
            <div style={rp.meterList}>
              {meters.slice(0, 80).map((m) => (
                <div key={m.id} style={rp.meterItem}>
                  <span style={rp.meterCode}>{m.meter_code || "—"}</span>
                  <span style={rp.meterAddr}>{m.address || ""}</span>
                  <button style={rp.removeBtn} onClick={() => onRemove(m.id)}>
                    <X size={10} />
                  </button>
                </div>
              ))}
              {meters.length > 80 && (
                <div style={rp.moreLabel}>+{meters.length - 80} más…</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {error && <div style={rp.errorMsg}>{error}</div>}
      {success && (
        <div style={rp.successMsg}>
          <CheckCircle2 size={14} style={{ marginRight: 6 }} />
          Ruta creada correctamente
        </div>
      )}
      <div style={rp.footer}>
        <button style={rp.cancelBtn} onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button
          style={{ ...rp.saveBtn, ...(saving || meters.length === 0 ? rp.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving || meters.length === 0}
        >
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? "Guardando…" : "Crear ruta"}
        </button>
      </div>
    </div>
  );
}

// ── Edit panel ───────────────────────────────────────────────────────────────
function EditPanel({ meter, onClose, onSaved }) {
  const [form, setForm] = useState({
    meter_code: meter.meter_code || "",
    address: meter.address || "",
    notes: meter.notes || "",
    latitude: meter.latitude ?? "",
    longitude: meter.longitude ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        meter_code: form.meter_code.trim() || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
        latitude: parseFloat(form.latitude) || null,
        longitude: parseFloat(form.longitude) || null,
      };
      await updateReading(meter.id, payload);
      setSuccess(true);
      setTimeout(() => {
        onSaved({ ...meter, ...payload });
        setSuccess(false);
      }, 800);
    } catch (e) {
      setError(e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  function field(label, key, type = "text") {
    return (
      <div style={ep.field}>
        <label style={ep.label}>{label}</label>
        <input
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          style={ep.input}
        />
      </div>
    );
  }

  return (
    <div style={ep.panel}>
      <div style={ep.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Edit2 size={16} color="#D4620D" />
          <span style={ep.title}>Editar Medidor</span>
        </div>
        <button style={ep.closeBtn} onClick={onClose}><X size={16} /></button>
      </div>
      <div style={ep.meta}>
        <span style={ep.metaChip}>ID: {meter.id?.slice(0, 8)}…</span>
        {meter.user_name && <span style={ep.metaChip}>Auditor: {meter.user_name}</span>}
      </div>
      <div style={ep.body}>
        {field("Código de medidor", "meter_code")}
        {field("Dirección", "address")}
        {field("Notas", "notes")}
        {field("Latitud", "latitude", "number")}
        {field("Longitud", "longitude", "number")}
      </div>
      {error   && <div style={ep.errorMsg}>{error}</div>}
      {success && <div style={ep.successMsg}>Guardado correctamente</div>}
      <div style={ep.footer}>
        <button style={ep.cancelBtn} onClick={onClose} disabled={saving}>Cancelar</button>
        <button
          style={{ ...ep.saveBtn, ...(saving ? ep.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          <Save size={14} style={{ marginRight: 6 }} />
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function MapaOptimizadoPage() {
  const [mapMode, setMapMode]     = useState("clusters");
  const [clusters, setClusters]   = useState([]);
  const [points, setPoints]       = useState([]);
  const [selected, setSelected]   = useState(null);   // medidor en edición
  const [loading, setLoading]     = useState(false);
  const [pointCount, setPointCount] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [tarifas, setTarifas]     = useState(null);

  // Route creation mode
  const [routeMode, setRouteMode]               = useState(false);
  const [areaSelectMode, setAreaSelectMode]     = useState(false);
  const [routeSelection, setRouteSelection]     = useState(new Map()); // id → meter
  const [selectedClusterKeys, setSelectedClusterKeys] = useState(new Set()); // "lat,lng" exactos
  const [fetchingCluster, setFetchingCluster]   = useState(false);

  // Filters
  const [filters, setFilters] = useState({ userId: "", from: "", to: "", zone: "", meterCode: "", segmentacion: "" });
  const [appliedFilters, setAppliedFilters] = useState({});

  // Map refs
  const mapRef      = useRef(null);
  const boundsRef   = useRef(null);
  const zoomRef     = useRef(13);
  const debounceRef = useRef(null);

  useEffect(() => {
    getEmployees()
      .then((e) =>
        setEmployees(
          e.filter((u) => u.active && u.role !== "admin" && u.role !== "admin-plus")
        )
      )
      .catch(() => {});
    getTarifas().then(setTarifas).catch(() => {});
  }, []);

  // ── Fetch viewport data ───────────────────────────────────────────────────
  const fetchData = useCallback(async (bounds, zoom, currentFilters) => {
    if (!bounds) return;
    setLoading(true);
    const bbox = {
      minLat: bounds.getSouth(), maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),  maxLng: bounds.getEast(),
    };
    try {
      // Sin agrupamiento — siempre puntos individuales
      const res = await getReadingsForMap({ ...bbox, ...currentFilters, limit: 5000 });
      const data = Array.isArray(res) ? res : res.data ?? [];
      setPoints(data);
      setPointCount(data.length);
      setClusters([]);
      setMapMode("points");
    } catch (err) {
      console.error("Error fetching map data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBoundsChange = useCallback((map) => {
    mapRef.current    = map;
    boundsRef.current = map.getBounds();
    zoomRef.current   = map.getZoom();
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(boundsRef.current, zoomRef.current, appliedFilters);
    }, DEBOUNCE_MS);
  }, [fetchData, appliedFilters]);

  useEffect(() => {
    if (boundsRef.current) fetchData(boundsRef.current, zoomRef.current, appliedFilters);
  }, [appliedFilters, fetchData]);

  // ── Filters ───────────────────────────────────────────────────────────────
  function applyFilters() {
    const f = {};
    if (filters.userId)       f.userId       = filters.userId;
    if (filters.from)         f.from         = filters.from;
    if (filters.to)           f.to           = filters.to;
    if (filters.zone)         f.zone         = filters.zone;
    if (filters.meterCode)    f.meterCode    = filters.meterCode;
    if (filters.segmentacion) f.segmentacion = filters.segmentacion;
    setAppliedFilters(f);
  }
  function clearFilters() {
    setFilters({ userId: "", from: "", to: "", zone: "", meterCode: "", segmentacion: "" });
    setAppliedFilters({});
  }

  // ── Edit ──────────────────────────────────────────────────────────────────
  function handleMeterSaved(updated) {
    setPoints((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setSelected(updated);
  }

  // ── Route mode ────────────────────────────────────────────────────────────
  function toggleRouteMode() {
    setRouteMode((prev) => {
      if (prev) {
        setRouteSelection(new Map());
        setSelectedClusterKeys(new Set());
        setAreaSelectMode(false);
      }
      return !prev;
    });
    setSelected(null);
  }

  function toggleRoutePoint(meter) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      if (next.has(meter.id)) next.delete(meter.id);
      else next.set(meter.id, meter);
      return next;
    });
  }

  function removeFromRoute(id) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }

  async function handleClusterCtrlSelect(cluster) {
    setFetchingCluster(true);
    try {
      // Bbox exacto de la celda del grid — misma fórmula que el backend
      const bbox = exactClusterBbox(cluster.lat, cluster.lng, zoomRef.current);
      const res = await getReadingsForMap({
        ...bbox,
        limit: 10000, // el bbox es exacto; no puede traer más de lo que tiene la celda
        ...appliedFilters,
      });
      const data = Array.isArray(res) ? res : res.data ?? [];
      setRouteSelection((prev) => {
        const next = new Map(prev);
        data.forEach((m) => next.set(m.id, m));
        return next;
      });
      setSelectedClusterKeys((prev) => new Set([...prev, `${cluster.lat},${cluster.lng}`]));
    } catch (err) {
      console.error("Error fetching cluster meters:", err);
    } finally {
      setFetchingCluster(false);
    }
  }

  function handleAreaSelected(meters) {
    setRouteSelection((prev) => {
      const next = new Map(prev);
      meters.forEach((m) => next.set(m.id, m));
      return next;
    });
    setAreaSelectMode(false);
  }

  function handleRouteCreated() {
    setRouteMode(false);
    setAreaSelectMode(false);
    setRouteSelection(new Map());
    setSelectedClusterKeys(new Set());
    setSelected(null);
  }

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasFilters      = Object.keys(appliedFilters).length > 0;
  const selectionMeters = Array.from(routeSelection.values());

  // Cluster pintado solo si fue explícitamente Ctrl+Clicado (posición exacta del servidor)
  function clusterIsSelected(cluster) {
    return routeMode && selectedClusterKeys.has(`${cluster.lat},${cluster.lng}`);
  }
  const panelOpen = routeMode || (selected && !routeMode);

  return (
    <div style={s.page}>
      {/* ── Top bar ── */}
      <div style={s.topBar}>
        <div style={s.titleRow}>
          <MapPin size={20} color="#D4620D" />
          <h1 style={s.title}>Mapa Optimizado</h1>
          {(loading || fetchingCluster) && <RefreshCw size={15} style={s.spinner} />}
          <div style={s.statusChips}>
            <span style={s.chip}>
              <Layers size={12} style={{ marginRight: 4 }} />
              {`${pointCount.toLocaleString()} puntos`}
            </span>
            {hasFilters && (
              <span style={{ ...s.chip, background: "#FBF0E8", color: "#D4620D" }}>
                Filtros activos
              </span>
            )}
            {routeMode && (
              <span style={{ ...s.chip, background: "#dcfce7", color: "#15803d" }}>
                <Navigation2 size={11} style={{ marginRight: 4 }} />
                Modo Ruta — {selectionMeters.length} seleccionados
              </span>
            )}
          </div>

          {/* Route mode toggle */}
          <button
            style={{ ...s.routeBtn, ...(routeMode ? s.routeBtnActive : {}) }}
            onClick={toggleRouteMode}
          >
            <Navigation2 size={14} style={{ marginRight: 6 }} />
            {routeMode ? "Cancelar ruta" : "Crear ruta"}
          </button>

          {/* Area select toggle — solo visible en modo ruta */}
          {routeMode && (
            <button
              style={{ ...s.areaBtn, ...(areaSelectMode ? s.areaBtnActive : {}) }}
              onClick={() => setAreaSelectMode((p) => !p)}
              title="Arrastrá para seleccionar un área"
            >
              <RectangleHorizontal size={14} style={{ marginRight: 6 }} />
              {areaSelectMode ? "Cancelar área" : "Seleccionar área"}
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={s.filters}>
          <select
            style={s.select}
            value={filters.userId}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
          >
            <option value="">Todos los auditores</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <input type="date" style={s.input} value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          <input type="date" style={s.input} value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          <select style={s.select} value={filters.zone}
            onChange={(e) => setFilters((f) => ({ ...f, zone: e.target.value }))}
          >
            <option value="">Todas las zonas</option>
            {["1","2","3","4"].map((z) => <option key={z} value={z}>Zona {z}</option>)}
          </select>
          <select
            style={s.select}
            value={filters.segmentacion}
            onChange={(e) => setFilters((f) => ({ ...f, segmentacion: e.target.value }))}
          >
            <option value="">Todas las segmentaciones</option>
            <option value="residencial">🟢 Residencial</option>
            <option value="comercial">🔵 Comercial</option>
          </select>
          <input
            style={s.input}
            placeholder="Código medidor…"
            value={filters.meterCode}
            onChange={(e) => setFilters((f) => ({ ...f, meterCode: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
          />
          <button style={s.searchBtn} onClick={applyFilters}>
            <Search size={14} style={{ marginRight: 6 }} />Buscar
          </button>
          {hasFilters && (
            <button style={s.clearBtn} onClick={clearFilters}><X size={14} /></button>
          )}
        </div>

        {/* Hint */}
        <div style={s.hint}>
          <Info size={12} style={{ marginRight: 4, flexShrink: 0 }} />
          {routeMode
            ? areaSelectMode
              ? "Área: arrastrá sobre el mapa para seleccionar todos los medidores del rectángulo"
              : "Clic en punto · Ctrl+Clic en cluster · Botón 'Seleccionar área' para arrastrar un rectángulo"
            : `Clic en punto para editar`}
        </div>
      </div>

      {/* ── Map + panel ── */}
      <div style={s.mapRow}>
        <div style={{ ...s.mapWrap, width: panelOpen ? "calc(100% - 340px)" : "100%" }}>
          <MapContainer
            center={[-34.6, -58.45]}
            zoom={12}
            style={{ width: "100%", height: "100%" }}
            preferCanvas={true}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              maxZoom={19}
            />
            <MapEventsHandler onBoundsChange={handleBoundsChange} />

            {/* Selección por área */}
            <AreaSelector
              active={routeMode && areaSelectMode}
              appliedFilters={appliedFilters}
              onSelected={handleAreaSelected}
              onFetchStart={() => setFetchingCluster(true)}
              onFetchEnd={() => setFetchingCluster(false)}
            />

            {/* Puntos individuales — sin agrupamiento */}
            {points.map((r) => (
              <MeterDot
                key={r.id}
                reading={r}
                isEditSelected={!routeMode && selected?.id === r.id}
                isRouteSelected={routeSelection.has(r.id)}
                routeMode={routeMode}
                onEditClick={setSelected}
                onRouteToggle={toggleRoutePoint}
              />
            ))}

            {/* Overlay de IDs seleccionados — capa Leaflet nativa, sin flickering */}
            <SelectedMetersOverlay
              routeSelection={routeSelection}
              routeMode={routeMode}
              onToggle={toggleRoutePoint}
            />
          </MapContainer>
        </div>

        {/* Route panel (modo ruta activo) */}
        {routeMode && (
          <RoutePanel
            selection={routeSelection}
            employees={employees}
            tarifas={tarifas}
            onRemove={removeFromRoute}
            onClear={() => { setRouteSelection(new Map()); setSelectedClusterKeys(new Set()); }}
            onClose={toggleRouteMode}
            onRouteCreated={handleRouteCreated}
          />
        )}

        {/* Edit panel (modo normal, punto seleccionado) */}
        {!routeMode && selected && (
          <EditPanel
            key={selected.id}
            meter={selected}
            onClose={() => setSelected(null)}
            onSaved={handleMeterSaved}
          />
        )}
      </div>
    </div>
  );
}

// ── Page styles ───────────────────────────────────────────────────────────────
const s = {
  page: { display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" },
  topBar: {
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: 14, padding: "14px 18px", marginBottom: 12, flexShrink: 0,
  },
  titleRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" },
  title: { fontSize: 18, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.4px", margin: 0 },
  spinner: { color: "#D4620D", animation: "spin 1s linear infinite" },
  statusChips: { display: "flex", gap: 8, flex: 1, flexWrap: "wrap" },
  chip: {
    display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700,
    background: "var(--bg-subtle)", color: "var(--text-2)", borderRadius: 100, padding: "3px 10px",
  },
  routeBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 16px",
    borderRadius: 8, border: "2px solid #16a34a", background: "transparent",
    color: "#16a34a", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif", transition: "all 0.15s",
  },
  routeBtnActive: {
    background: "#16a34a", color: "#fff",
  },
  areaBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 14px",
    borderRadius: 8, border: "2px solid #0891b2", background: "transparent",
    color: "#0891b2", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif", transition: "all 0.15s",
  },
  areaBtnActive: {
    background: "#0891b2", color: "#fff",
  },
  filters: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 },
  select: {
    height: 34, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", cursor: "pointer", minWidth: 150,
  },
  input: {
    height: 34, padding: "0 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif",
  },
  searchBtn: {
    display: "inline-flex", alignItems: "center", height: 34, padding: "0 16px",
    borderRadius: 8, border: "none", background: "#D4620D", color: "#fff",
    fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  clearBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    height: 34, width: 34, borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  hint: { display: "flex", alignItems: "center", fontSize: 11, color: "var(--text-3)" },
  mapRow: { flex: 1, display: "flex", gap: 12, minHeight: 0 },
  mapWrap: {
    borderRadius: 14, overflow: "hidden", border: "1px solid var(--border)",
    transition: "width 0.2s ease", minHeight: 0,
  },
};

// ── Route panel styles ────────────────────────────────────────────────────────
const rp = {
  panel: {
    width: 328, flexShrink: 0, background: "var(--bg-card)",
    border: "1px solid var(--border)", borderRadius: 14,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  title: { fontSize: 14, fontWeight: 800, color: "var(--text)" },
  closeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  instructions: {
    display: "flex", alignItems: "flex-start", fontSize: 11, color: "var(--text-2)",
    padding: "10px 16px", borderBottom: "1px solid var(--border)",
    background: "#f0fdf4", flexShrink: 0,
  },
  counter: {
    display: "flex", alignItems: "center", gap: 8, padding: "12px 16px",
    borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  counterNum: { fontSize: 22, fontWeight: 800, color: "#16a34a" },
  counterLabel: { fontSize: 12, color: "var(--text-2)", flex: 1 },
  clearSelBtn: {
    display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 700,
    padding: "4px 10px", borderRadius: 7, border: "1px solid #fca5a5",
    background: "#fef2f2", color: "#dc2626", cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  body: { flex: 1, overflowY: "auto", padding: "14px 16px 8px", display: "flex", flexDirection: "column", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.4px" },
  input: {
    padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
  },
  meterList: {
    maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)",
    borderRadius: 8, background: "var(--bg)",
  },
  meterItem: {
    display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
    borderBottom: "1px solid var(--border)", fontSize: 12,
  },
  meterCode: { fontWeight: 700, color: "var(--text)", minWidth: 70 },
  meterAddr: { flex: 1, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  removeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 4, border: "none",
    background: "var(--bg-subtle)", color: "var(--text-3)", cursor: "pointer", flexShrink: 0,
  },
  moreLabel: { padding: "6px 10px", fontSize: 11, color: "var(--text-3)", fontStyle: "italic" },
  priceBox: {
    background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
    padding: "10px 14px", display: "flex", flexDirection: "column", gap: 4,
  },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  priceLabel: { fontSize: 12, color: "#15803d" },
  priceZone: { fontSize: 11, color: "#16a34a", opacity: 0.8 },
  priceTotal: { fontSize: 14, fontWeight: 800, color: "#15803d", marginTop: 2 },
  errorMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  successMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#f0fdf4", color: "#16a34a", borderRadius: 8, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center" },
  footer: { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)", flexShrink: 0 },
  cancelBtn: {
    flex: 1, padding: "9px 0", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  saveBtn: {
    flex: 2, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "9px 0", borderRadius: 9, border: "none", background: "#16a34a",
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  saveBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};

// ── Edit panel styles ─────────────────────────────────────────────────────────
const ep = {
  panel: {
    width: 328, flexShrink: 0, background: "var(--bg-card)",
    border: "1px solid var(--border)", borderRadius: 14,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0,
  },
  title: { fontSize: 14, fontWeight: 800, color: "var(--text)" },
  closeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
  },
  meta: { display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 },
  metaChip: { fontSize: 10, fontWeight: 700, background: "var(--bg-subtle)", color: "var(--text-2)", borderRadius: 100, padding: "2px 8px" },
  body: { flex: 1, overflowY: "auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.4px" },
  input: {
    padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)", fontSize: 13,
    fontFamily: "Montserrat, sans-serif", outline: "none", width: "100%", boxSizing: "border-box",
  },
  errorMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  successMsg: { margin: "0 16px 8px", padding: "8px 12px", background: "#f0fdf4", color: "#16a34a", borderRadius: 8, fontSize: 12, fontWeight: 600 },
  footer: { display: "flex", gap: 8, padding: "12px 16px", borderTop: "1px solid var(--border)", flexShrink: 0 },
  cancelBtn: {
    flex: 1, padding: "9px 0", borderRadius: 9, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  saveBtn: {
    flex: 2, display: "flex", alignItems: "center", justifyContent: "center",
    padding: "9px 0", borderRadius: 9, border: "none", background: "#D4620D",
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  saveBtnDisabled: { opacity: 0.6, cursor: "not-allowed" },
};
